import omit from 'lodash/omit';
import orderBy from 'lodash/orderBy';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { omitBy } from 'lodash';
import queryString from 'query-string';

import { useApiQuery, useFetch } from '@/modules/common';
import type { EventFiltersType } from '@/modules/eventsV2/hooks';

import { InventoryReportType } from '../constants';
import { EventProductsResponse } from '../types';

const fileNameMap = {
  [InventoryReportType.CheckedOutBags]: 'Checked-Out Bags Report',
  [InventoryReportType.BackStockInventory]: 'Back Stock Inventory Report',
};

export function useEventProducts(eventId: string, stationId: string, filters: EventFiltersType) {
  const fetchClient = useFetch();
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const baseUrl = `/events/${eventId}/stations/${stationId}/products`;

  const { data: products, isFetching } = useApiQuery<EventProductsResponse>(
    `${baseUrl}?${queryString.stringify(
      omitBy(filters, (v) => v === ''),
      { arrayFormat: 'bracket' }
    )}&pageSize=1000`,
    {
      queryKey: ['eventProducts', eventId, stationId, filters],
      initialData: { items: [] },
      enabled: !!stationId,
    }
  );

  const uploadEventProducts = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await fetchClient(baseUrl, {
        body: formData,
        method: 'PATCH',
        asFormData: true,
      });
    } catch (error) {
      toast.error(error.message);
      throw new Error(error.message);
    }
  };

  const getGenerateReportUrl = async (reportType: InventoryReportType) => {
    setIsGeneratingReport(true);
    try {
      const response = await fetchClient(`${baseUrl}/report/${reportType}`, {
        method: 'GET',
        asFormData: true,
        responseType: 'blob',
      });
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${fileNameMap[reportType]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const formattedProducts = useMemo(() => {
    const list = products?.items
      .filter((product) => product.sizes?.length > 0)
      .map((product) => {
        if (product.sizes.length > 1) {
          return product.sizes.map((size) => ({
            ...omit(product, 'sizes'),
            size: size,
            totalAmount: size?.amount || 0,
          }));
        }

        return {
          ...omit(product, 'sizes'),
          size: product.sizes[0],
          totalAmount: product?.sizes[0]?.amount || 0,
        };
      });

    return orderBy(list.flat(), 'size.size', 'asc');
  }, [products]);

  return {
    isFetching,
    uploadEventProducts,
    getGenerateReportUrl,
    isGeneratingReport,
    products: formattedProducts,
  } as const;
}
